import React, { useState } from 'react';
import { ShowController, Link, useQueryWithStore, useNotify, useRefresh } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MuiLink from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import moment from 'moment';

import useSupplyStyles from '../controls/SupplyStyles';
import supplyPost from '../controls/supplyApi';
import AddLotDialog from './AddLotDialog';
import ErrorBoundary from '../ErrorBoundary';
import ViewListIcon from '@material-ui/icons/ViewList';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const fmt = value => (value ? moment(value).format('MM/DD/YYYY') : '-');

const STATUS_LABEL = { OPEN: 'Open', FINALIZED: 'Finalized', CANCELLED: 'Cancelled' };

/** Receipt lines on this manifest. */
const ShipmentLines = ({ shipment }) => {
    const classes = useSupplyStyles();
    const { data, loaded } = useQueryWithStore({
        type: 'getList',
        resource: 'supplylots',
        payload: {
            filter: { shipment_id: shipment.id },
            sort: { field: 'lot_no', order: 'ASC' },
            pagination: { page: 1, perPage: 200 },
        },
    });

    if (!loaded) { return null; }
    const lines = data || [];

    return (
        <React.Fragment>
            <Typography className={classes.sectionTitle}>Receipt Lines ({lines.length})</Typography>
            <Table size="small">
                <TableHead className={classes.tableHead}>
                    <TableRow>
                        <TableCell className={classes.tableHeadCell}>Supply Type</TableCell>
                        <TableCell className={classes.tableHeadCell}>Lot #</TableCell>
                        <TableCell className={classes.tableHeadCell}>Expiry Date</TableCell>
                        <TableCell className={classes.tableHeadCell}>Received Qty</TableCell>
                        <TableCell className={classes.tableHeadCell}>On Hand</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {lines.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5}>No lots on this manifest yet.</TableCell>
                        </TableRow>
                    )}
                    {lines.map(line => (
                        <TableRow key={line.id}>
                            <TableCell>{line.supply_type}</TableCell>
                            <TableCell>
                                <Link to={`/supplyreceipts/${line.id}/show`}>{line.lot_no}</Link>
                            </TableCell>
                            <TableCell>{fmt(line.expiry_date)}</TableCell>
                            <TableCell>{line.received_qty}</TableCell>
                            <TableCell>{line.qty_total}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </React.Fragment>
    );
};

const ShipmentShow = props => {
    const classes = useSupplyStyles();
    const notify = useNotify();
    const refresh = useRefresh();
    const [addOpen, setAddOpen] = useState(false);

    const finalize = id => {
        supplyPost(`supplyshipments/${id}/finalize`, {})
            .then(() => {
                notify('Manifest finalized - stock is now in inventory');
                refresh();
            })
            .catch(error => {
                notify(
                    (error && error.body) || (error && error.message) || 'Could not finalize',
                    'warning'
                );
            });
    };

    return (
        <ErrorBoundary>
            <ShowController {...props} title=" ">
                {({ record }) =>
                    record && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <Card className={classes.asideCard}>
                                    <CardContent>
                                        <Typography className={classes.asideTitle}>
                                            Supply Shipment
                                        </Typography>

                                        <Typography className={classes.asideLabel}>Manifest #:</Typography>
                                        <Typography className={classes.asideValue}>
                                            {record.manifest_no}
                                        </Typography>

                                        <Typography className={classes.asideLabel}>Status:</Typography>
                                        <Typography className={classes.asideValue}>
                                            {STATUS_LABEL[record.status] || record.status}
                                        </Typography>

                                        <Typography className={classes.asideLabel}>Origin:</Typography>
                                        <Typography className={classes.asideValue}>
                                            {record.origin || '-'}
                                        </Typography>

                                        <Typography className={classes.asideTitle}>Actions</Typography>

                                        {record.status === 'OPEN' && (
                                            <React.Fragment>
                                                <MuiLink
                                                    component="button"
                                                    className={classes.actionLink}
                                                    onClick={() => setAddOpen(true)}
                                                >
                                                    Add Lot
                                                </MuiLink>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    className={classes.searchButton}
                                                    onClick={() => finalize(record.id)}
                                                >
                                                    Finish
                                                </Button>
                                            </React.Fragment>
                                        )}

                                        <Button
                                            component={RouterLink}
                                            to={{
                                                pathname: '/supplyshipments',
                                            }}
                                            size="small"
                                            fullWidth
                                            startIcon={<ViewListIcon />}
                                            endIcon={<ChevronRightIcon />}
                                            className={classes.navButton}
                                        >
                                          Shipment List  
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={9}>
                                <Card className={classes.asideCardWide}>
                                    <CardContent>
                                        <Typography className={classes.sectionTitle}>Summary</Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2">
                                                    <b>Manifest #:</b> {record.manifest_no}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <b>Origin Type:</b> {record.origin_type}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <b>Origin:</b> {record.origin || '-'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <b>Carrier:</b> {record.carrier || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2">
                                                    <b>Ship Date:</b> {fmt(record.ship_date)}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <b>Receipt Date:</b> {fmt(record.receipt_date)}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <b>PO #:</b> {record.po_no || '-'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <b>Ref #:</b> {record.ref_no || '-'}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <ShipmentLines shipment={record} />
                                    </CardContent>
                                </Card>
                            </Grid>

                            <AddLotDialog
                                open={addOpen}
                                shipment={record}
                                onClose={() => setAddOpen(false)}
                            />
                        </Grid>
                    )
                }
            </ShowController>
        </ErrorBoundary>
    );
};

export default ShipmentShow;
